import classNames from 'classnames';
import {action, computed, observable} from 'mobx';
import {observer, Provider} from 'mobx-react';
import {WithRouterProps} from 'next/dist/client/with-router';
import {withRouter} from 'next/router';
import React from 'react';

import {AsyncPriceCourseCardContext, BrowseCourse} from '@udemy/browse-course';
import {
    GwgCarousel,
    GwgAccelerator,
    AcademySelected,
    AcademyPresented,
} from '@udemy/career-academies';
import {DiscoveryUnit} from '@udemy/discovery-api';
import {Tracker, TrackImpression} from '@udemy/event-tracking';
import {FunnelLogContextProvider} from '@udemy/funnel-tracking';
import {FeatureVariantAssignment} from '@udemy/graphql';
import {withMatchMedia} from '@udemy/hooks';
import {withI18n, WithI18nProps} from '@udemy/i18n';
import {MarketplaceOnly} from '@udemy/react-brand-components';
import {ContextualHeader} from '@udemy/react-browse-contextual-header';
import {
    CertificationBundleUnit,
    isBundleInSet,
    CertificationExamVoucherBannerWrapper,
    VoucherUIRegion,
    CertificationVoucherBundleUnit,
    HydratedCertificationBundle,
} from '@udemy/react-certification-preparation';
import {CourseGuidanceTakeShortAssessmentEntrypoint} from '@udemy/react-course-highlights';
import {
    AlternateHeadline,
    DiscoveryUnitsContainerStore,
    ValueProps,
    HorizontalBundleUnit,
} from '@udemy/react-discovery-units';
import {ExperimentImpressionEvent} from '@udemy/react-experimentation-provider';
import {EntryPointPopUp} from '@udemy/react-globalization-components/course-guidance';
import {
    type RelatedCategoryItem,
    extractCategoryAndSubcategoryItems,
    fetchRecommendationSections,
    RecommendationContextIds,
    RecommendationSourceObjectTypes,
} from '@udemy/react-recommendations';
import {udSentry} from '@udemy/sentry';
import {serverOrClient} from '@udemy/shared-utils';
import {
    SubscriptionPlanContext,
    SubscriptionPlanSubscriberState,
    ConsumerSubscriptionGwGStandalonePlan,
    ConsumerSubscriptionGwGPersonalPlan,
} from '@udemy/subscription-graphql';
import {
    BrandWithOrganization,
    CUSTOM_EVENT_NAME_PROVIDER_BRIDGE,
    withUDData,
    WithUDDataProps,
} from '@udemy/ud-data';

import {RelatedCategoryLinks} from 'src/components/related-category-links/related-category-links.react-component';
import {SkeletonTitle} from 'udemy-django-static/js/browse/components/discovery-units-container/discovery-units-skeletons.react-component';
import {
    AGGREGATION_TYPE_PRICE,
    PAGE_TYPE_ORG_TOPIC,
    PAGE_TYPE_SUBS_TOPIC,
    PAGE_TYPE_TOPIC,
} from 'udemy-django-static/js/browse/lib/constants';
import {CertificationModel} from 'udemy-django-static/js/open-badges/certification.mobx-model';
import {CertificationUnit} from 'udemy-django-static/js/open-badges/open-badge-unit/certification-unit.react-component';
import {CONTEXT_TYPES} from 'udemy-django-static/js/organization-common/resource-context-menu/constants';
import ResourceContextMenuProvider from 'udemy-django-static/js/organization-common/resource-context-menu/resource-context-menu-provider.react-component';

import {LearnMoreSection} from './components/learn-more-section.react-component';
import {TopicCertificationBanner} from './components/topic-certification-banner.react-component';
import {
    TopicDiscoveryListContainerWrapper,
    TopicDiscoveryUnitsContainerWrapper,
    TopicDiscoveryUnitsContainerWrapperLoader,
} from './components/topic-discovery-units-wrapper.react-component';
import {TopicEnrollmentStats} from './components/topic-enrollment-stats.react-component';
import {
    BUNDLE_DISCOUNT_CODE,
    PRESET_ALL,
    PRESET_FREE,
    SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE,
    SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS,
} from './constants';
import {TopicPageViewEvent} from './events';
import styles from './topic.module.less';
import {isOrgTopicPage, isSubsTopicPage, TopicPageType} from './types/page';
import {TopicContext} from './types/topic';
import {getPageObjectIdFromTopic} from './utils';

export interface TopicPageExperimentVariants {
    isExperimentConfigsLoading?: boolean;
    showCodingExercisesBadge?: boolean;
    showUpdatedPPTopicPage?: boolean;
    showPersonalPlanBadge?: boolean;
    showCertificationBanner?: boolean;
    useStandardizeMobileBrowseCarousel?: boolean;
    browseAggregatorPageEnabled?: boolean;
    showCourseGuidance?: boolean;
    fullQuickViewBoxFeature?: boolean;
    showThreeCardVerticalLayout?: boolean;
    showAddToCartInThreeCardLayout?: boolean;
    showTopicSpecialBundlePricing?: FeatureVariantAssignment;
    isTopicVoucherModuleAboveBundle?: FeatureVariantAssignment;
    certVouchersShowAddToCart?: boolean;
    certificationVoucherBundleUnitFeature?: FeatureVariantAssignment;
}

export interface TopicProps
    extends TopicContext,
        TopicPageExperimentVariants,
        WithUDDataProps,
        WithI18nProps {
    subsCollectionIds?: string;
    isConsumerSubsSubscriber: boolean;
    isSubsLoading?: boolean;
    // In the server side, withMatchMedia is returning null
    isMobile?: boolean | null;
    certification?: CertificationModel;
    certificationBundle?: HydratedCertificationBundle;
    genericTopicBundleCourses?: BrowseCourse[];
    enableLabsInPersonalPlan?: boolean;
    enableAssessmentsInPersonalPlan?: boolean;
    isLowSupplyTopicPage?: boolean;
    activeSubscriptionPlan?: SubscriptionPlanSubscriberState;
    gwgSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGStandalonePlan>;
    gwgPlusPPSubscriptionPlan?: SubscriptionPlanContext<ConsumerSubscriptionGwGPersonalPlan>;
    showGwgCarousel?: boolean;
    gwgCarouselAccelerator?: GwgAccelerator;
    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    isRecommendationsEnabled?: boolean;
    recommendationUnits?: Partial<DiscoveryUnit>[];
    isRecommendationsLoading?: boolean;
    onLoadMore?: (title: string) => Promise<Partial<DiscoveryUnit>>;
}

@withMatchMedia({isMobile: 'mobile-max'})
@observer
export class InternalTopic extends React.Component<TopicProps & WithRouterProps> {
    private hasPublishedSpecialBundlePricingImpressionEvent = false;

    static defaultProps = {
        presetValue: PRESET_ALL,
        relatedPages: [],
        questionsAndAnswers: [],
        showPersonalPlanBadge: false,
        showCodingExercisesBadge: false,
        showUpdatedPPTopicPage: false,
        isMobile: false,
        enableLabsInPersonalPlan: false,
        enableAssessmentsInPersonalPlan: false,
        showCourseGuidance: false,
        fullQuickViewBoxFeature: false,
        showThreeCardVerticalLayout: false,
        showAddToCartInThreeCardLayout: false,
        isLowSupplyTopicPage: false,
        showGwgCarousel: false,
    };

    async componentDidMount() {
        this.fetchTopicContextData(this.props.topic.id);
        Tracker.publishEvent(
            new TopicPageViewEvent({
                topicId: getPageObjectIdFromTopic(this.props.topic),
            }),
        );
        this.updatePageContext();
        this.triggerGenericBundleImpressionEvent();
    }

    componentDidUpdate(prevProps: Readonly<TopicProps & WithRouterProps>) {
        if (
            prevProps.udData.Config.marketplace_country?.id !=
            this.props.udData.Config.marketplace_country?.id
        ) {
            this.updatePageContext();
        }
        this.triggerGenericBundleImpressionEvent();
    }

    private ref = React.createRef<HTMLDivElement>();
    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    @observable discoveryUnitsStore: DiscoveryUnitsContainerStore | undefined;
    @observable relatedCategories?: RelatedCategoryItem[] = undefined;

    @action
    private async fetchTopicContextData(topicId: string) {
        try {
            const contextualHeaderData = await fetchRecommendationSections({
                params: {
                    contextId: RecommendationContextIds.TOPIC_CONTEXTUAL_HEADER,
                    sourceObjects: [{type: RecommendationSourceObjectTypes.LABEL, id: topicId}],
                },
            });
            this.relatedCategories = extractCategoryAndSubcategoryItems(contextualHeaderData);
        } catch (e) {
            udSentry.captureException(e);
            this.relatedCategories = [];
        }
    }

    private resourceContextMenuProps = {
        context: CONTEXT_TYPES.ALL_COURSES,
    };

    updatePageContext = () => {
        const {udData, topic} = this.props;
        // This trigger braze, it gets the value of marketplace_country from udData.Config
        if (topic.id && udData.Config.marketplace_country?.id) {
            const updatedUDData = {
                marketplace_country: {},
                pageContext: {
                    entityId: topic.id.toString(),
                },
            };

            setTimeout(() => {
                window.dispatchEvent(
                    new CustomEvent(CUSTOM_EVENT_NAME_PROVIDER_BRIDGE, {detail: updatedUDData}),
                );
            }, 0);
        }
    };

    triggerGenericBundleImpressionEvent = () => {
        const {genericTopicBundleCourses, showTopicSpecialBundlePricing, udData, topic} =
            this.props;

        if (this.hasPublishedSpecialBundlePricingImpressionEvent) {
            return;
        }

        if (genericTopicBundleCourses) {
            const locale = udData.request.locale;
            const specialPricingTopicIds = SPECIAL_PRICING_TOPIC_IDS_BY_LOCALE[locale];
            const topicId = Number(topic.id);

            if (specialPricingTopicIds) {
                const isSpecialPricingTopic = specialPricingTopicIds.includes(topicId);

                if (isSpecialPricingTopic && showTopicSpecialBundlePricing) {
                    Tracker.publishEvent(
                        new ExperimentImpressionEvent(
                            showTopicSpecialBundlePricing.experimentIds,
                            showTopicSpecialBundlePricing.isLocalDefault,
                        ),
                    );
                    this.hasPublishedSpecialBundlePricingImpressionEvent = true;
                }
            }
        }
    };

    @computed
    get isMXUser() {
        const {udData, isConsumerSubsSubscriber} = this.props;
        const {Config} = udData;
        return !isConsumerSubsSubscriber && !Config.brand.has_organization;
    }

    @computed
    get isEnglishLocale() {
        const {udData} = this.props;
        const {request} = udData;
        return request.locale === 'en_US';
    }

    @computed
    get isCertificationBundleInSet() {
        const {certificationBundle} = this.props;
        const certificationBundleSet =
            this.isMXUser && this.isEnglishLocale ? 'FIRST_EXPANSION' : 'INITIAL';
        return certificationBundle && isBundleInSet(certificationBundle, certificationBundleSet);
    }

    @computed
    get pageType() {
        const {udData, isConsumerSubsSubscriber} = this.props;
        const {Config} = udData;
        const hasOrganization = Config.brand.has_organization;

        let pageType: TopicPageType = PAGE_TYPE_TOPIC;
        if (isConsumerSubsSubscriber) {
            pageType = PAGE_TYPE_SUBS_TOPIC;
        }
        if (hasOrganization) {
            pageType = PAGE_TYPE_ORG_TOPIC;
        }
        return pageType;
    }

    @computed
    get arePageDataLoading() {
        return this.props.isSubsLoading || this.props.udData.isGlobalMeContextLoading;
    }

    @computed
    get showCertificationBanner() {
        return !!this.props.certification;
    }

    @computed
    get showContextualHeader() {
        return !this.showCertificationBanner && !!this.props.tertiaryDescription;
    }

    @computed
    get hasReplacementTitle() {
        return this.showCertificationBanner || this.showContextualHeader;
    }

    get localizedEnrollmentStat() {
        const enrollmentStat = this.props.enrollmentStat;
        if (!enrollmentStat) {
            return enrollmentStat;
        }
        // Strip all non-digit characters from the string.
        // This can be done because we expect it to be a whole number.
        const cleanedEnrollmentStat = enrollmentStat.replace(/\D/g, '');

        try {
            // Even if the enrollment stat is localized already, we want to do it again, to bring it in line with the
            // localization for other items.
            return parseInt(cleanedEnrollmentStat).toLocaleString(
                this.props.locale.replace('_', '-') || 'en-US',
            );
        } catch {
            return enrollmentStat;
        }
    }

    /**
     * Main purpose of this is that discussed below:
     * https://github.com/udemy/frontends-marketplace-experience/pull/1118/files#r1237231100
     */
    get primaryHeading() {
        const {topic, topicTitle, presetValue} = this.props;
        const showOnlyTopicName = isSubsTopicPage(this.pageType) || isOrgTopicPage(this.pageType);
        if (presetValue === PRESET_ALL && showOnlyTopicName) {
            // This is a localized name for the topic
            return topic.name;
        }
        // This is a localized title for the topic
        return topicTitle;
    }

    private getCategoryAndSubcategoryItems() {
        return this.relatedCategories;
    }

    private renderCertificationBanner() {
        if (this.props.certification) {
            /**
             * @remarks
             * We're not waiting for discoveryUnitStore to be initialized or loaded. If we've waited, it could
             * cause whole page to wait on just for the certification banner, just to not have CLS issues that can
             * be caused by the certification banner.
             *
             * Instead of that, we're just rendering the certification banner without waiting and let it render the
             * related categories and subcategories dynamically. So by this; we're not blocking the page load, and
             * we're not losing the CLS score that much (just a little)
             */

            const relatedCategoriesAndSubcategories = this.getCategoryAndSubcategoryItems();

            return (
                <TopicCertificationBanner
                    certification={this.props.certification}
                    enrollmentStat={this.localizedEnrollmentStat}
                    relatedCategoriesAndSubcategories={relatedCategoriesAndSubcategories}
                    className={'ud-container'}
                    isMobile={!!this.props.isMobile}
                    relatedCertificationBrowseAggregators={
                        this.props.relatedCertificationBrowseAggregators
                    }
                    browseAggregatorPageEnabled={this.props.browseAggregatorPageEnabled}
                />
            );
        }
        return null;
    }

    private renderContextualHeader() {
        const relatedCatAndSubCat = this.getCategoryAndSubcategoryItems();

        return (
            <ContextualHeader
                className="ud-container"
                title={this.primaryHeading}
                description={this.props.tertiaryDescription ?? ''}
                enrollmentStat={this.localizedEnrollmentStat}
                numCourses={this.props.numCourses}
                numExercises={this.props.numExercises}
                rating={this.props.avgRating}
                relatedCategoriesAndSubcategories={relatedCatAndSubCat}
            />
        );
    }

    private renderTitle() {
        const {isMobile} = this.props;
        return (
            <AlternateHeadline
                title={this.primaryHeading}
                titleTag="h1"
                titleClass={isMobile ? 'ud-heading-serif-xl' : 'ud-heading-serif-xxl'}
                titleStyle={isMobile ? 'title-compact' : 'title'}
                layoutVariant={isMobile ? 'default' : 'large'}
            />
        );
    }

    private renderLoadingTitle() {
        /**
         * The alternate headline in the loading state is for the seo purposes.
         * We're only serving our pages to bots if we're in the mx and not logged in. But title is changing for below
         * user-product types:
         * - Logged out - MX
         * - Logged in - Subs
         * - Logged in - UFB
         *
         * But since we're using same page for those 3 pairs, we need to wait to load the title. But as you can see
         * we only serve Logged out - MX pair to the bots so actually there's only one title that bots can see.
         *
         * That's why, while loading, we're just injecting a screen reader only title for cover the waiting for getting
         * the subscription or ud me data in order to render correct title.
         */
        return (
            <>
                <SkeletonTitle className={styles['title-skeleton']} data-item-index={0} />
                <AlternateHeadline
                    title={this.primaryHeading}
                    className="ud-sr-only"
                    titleTag="h1"
                />
            </>
        );
    }

    private renderDiscoveryUnitsLoader() {
        return <TopicDiscoveryUnitsContainerWrapperLoader isMobile={!!this.props.isMobile} />;
    }

    private renderCategoryUnits() {
        const relatedCatAndSubCat = this.getCategoryAndSubcategoryItems();
        const {isMobile, enrollmentStat, topic} = this.props;

        return (
            <RelatedCategoryLinks
                className={
                    enrollmentStat
                        ? styles['related-categories-margin-sm']
                        : styles['related-categories-margin-lg']
                }
                isMobile={!!isMobile}
                relatedCategoriesAndSubcategories={relatedCatAndSubCat}
                topicTitle={topic.name}
            />
        );
    }

    private renderCourseGuidanceBanner() {
        if (this.props.showCourseGuidance) {
            return (
                <div className="component-margin" data-item-index={3}>
                    <CourseGuidanceTakeShortAssessmentEntrypoint />
                </div>
            );
        }
        return <></>;
    }

    private renderStaticUnits() {
        return (
            <>
                {this.props.showGwgCarousel && this.props.gwgCarouselAccelerator && (
                    <div className={classNames(styles['gwg-unit-wrapper'], 'component-margin')}>
                        <div className={classNames('ud-heading-xl', styles['gwg-unit-header'])}>
                            {gettext('Learn AI with Google')}
                        </div>
                        <TrackImpression
                            trackFunc={() => {
                                Tracker.publishEvent(
                                    new AcademyPresented({
                                        academySlug: this.props.gwgCarouselAccelerator?.slug ?? '',
                                        uiRegion: 'learn-ai-with-google',
                                    }),
                                );
                            }}
                        >
                            <div>
                                <GwgCarousel
                                    accelerator={this.props.gwgCarouselAccelerator}
                                    currentlySubscribedPlan={this.props.activeSubscriptionPlan}
                                    onEnrollmentClick={() => {
                                        Tracker.publishEvent(
                                            new AcademySelected({
                                                academySlug:
                                                    this.props.gwgCarouselAccelerator?.slug ?? '',
                                                uiRegion: 'learn-ai-with-google',
                                            }),
                                        );
                                    }}
                                />
                            </div>
                        </TrackImpression>
                    </div>
                )}
                {this.renderBundleUnit()}
            </>
        );
    }

    private renderBundleUnit() {
        const {udData, isConsumerSubsSubscriber} = this.props;
        const {Config} = udData;
        const locale = udData.request.locale;

        const isMXUser = !isConsumerSubsSubscriber && !Config.brand.has_organization;
        const isEnglishLocale = locale === 'en_US';

        // Expanded certification bundle set is only available for MX users in English locale
        const certificationBundleSet = isMXUser && isEnglishLocale ? 'FIRST_EXPANSION' : 'INITIAL';
        const isCertificationBundleInSet =
            this.props.certificationBundle &&
            isBundleInSet(this.props.certificationBundle, certificationBundleSet);

        // Generic bundles are only available for MX users
        const showGenericBundles = isMXUser;
        const showCertificationBundle = isMXUser && isEnglishLocale && isCertificationBundleInSet;

        const showSpecialBundlePricingExperimentEnabled =
            this.props.showTopicSpecialBundlePricing?.configuration?.enableSpecialBundlePricing ??
            false;

        const certVouchersShowAddToCart = this.props.certVouchersShowAddToCart;
        const showCertificationVoucherBundleUnit =
            this.props.certificationVoucherBundleUnitFeature?.configuration
                ?.showCertificationVoucherBundleUnit ?? false;

        // Cert bundle discount experiment props
        // These will be provided to the bundle unit only for cert bundles
        const specialBundleDiscountProps = {
            isBundleDiscountEnabled: true,
            bundleDiscountCode: BUNDLE_DISCOUNT_CODE,
        };

        const voucherContainerClass = classNames(styles['voucher-banner-bundle-unit-container'], {
            [styles['voucher-above']]:
                this.props.isTopicVoucherModuleAboveBundle?.configuration
                    ?.isVoucherModuleAbovebundle ?? false,
            [styles['voucher-below']]: !(
                this.props.isTopicVoucherModuleAboveBundle?.configuration
                    ?.isVoucherModuleAbovebundle ?? false
            ),
        });

        if (showCertificationBundle && this.props.certificationBundle) {
            // Voucher banner for English locale: render voucher banner following certification bundle unit rules
            // This shows the voucher banner only in English when certification bundle unit is present
            // Voucher banner positioning: determines if voucher banner renders above or below certification bundle unit
            // based on the experiment configuration (isTopicVoucherModuleAboveBundle)
            return (
                <div className={classNames(voucherContainerClass, 'component-margin')}>
                    {showCertificationVoucherBundleUnit ? (
                        <TrackImpression
                            visibilityThreshold={0}
                            trackFunc={() => {
                                if (this.props.certificationVoucherBundleUnitFeature) {
                                    const {experimentIds = [], isLocalDefault = false} =
                                        this.props.certificationVoucherBundleUnitFeature;
                                    Tracker.publishEvent(
                                        new ExperimentImpressionEvent(
                                            experimentIds,
                                            isLocalDefault,
                                        ),
                                    );
                                }
                            }}
                        >
                            <FunnelLogContextProvider pageType="topic">
                                <CertificationVoucherBundleUnit
                                    bundle={this.props.certificationBundle}
                                    {...SPECIAL_VOUCHER_BUNDLE_DISCOUNT_PROPS}
                                />
                            </FunnelLogContextProvider>
                        </TrackImpression>
                    ) : (
                        <TrackImpression
                            visibilityThreshold={0}
                            trackFunc={() => {
                                if (this.props.certificationVoucherBundleUnitFeature) {
                                    const {experimentIds = [], isLocalDefault = false} =
                                        this.props.certificationVoucherBundleUnitFeature;
                                    Tracker.publishEvent(
                                        new ExperimentImpressionEvent(
                                            experimentIds,
                                            isLocalDefault,
                                        ),
                                    );
                                }
                            }}
                        >
                            <div
                                className={classNames(styles['certification-bundle-unit-wrapper'])}
                                data-item-index={0}
                            >
                                <CertificationBundleUnit
                                    certificationBundle={this.props.certificationBundle}
                                    {...specialBundleDiscountProps}
                                />
                            </div>
                        </TrackImpression>
                    )}
                    <CertificationExamVoucherBannerWrapper
                        topicId={this.props.topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner
                        experimentData={this.props.isTopicVoucherModuleAboveBundle}
                        uiRegion={
                            this.props.isTopicVoucherModuleAboveBundle?.configuration
                                ?.isTopicVoucherModuleAbovebundle
                                ? ('above_course_bundle' as VoucherUIRegion)
                                : ('below_course_bundle' as VoucherUIRegion)
                        }
                        showAddToCart={certVouchersShowAddToCart}
                    />
                </div>
            );
        } else if (showGenericBundles && this.props.genericTopicBundleCourses) {
            return (
                <>
                    <div
                        className={classNames(
                            styles['certification-bundle-unit-wrapper'],
                            'component-margin',
                        )}
                        data-item-index={0}
                    >
                        <HorizontalBundleUnit
                            contents={this.props.genericTopicBundleCourses}
                            title={interpolate(
                                gettext(
                                    "Looking to advance your skills in %(topicName)s? We've got you.",
                                ),
                                {topicName: this.props.topic.name},
                                true,
                            )}
                            subtitle={gettext(
                                'Get everything you need to reach your goals in one convenient bundle.',
                            )}
                            valueProps={[
                                gettext('Top-rated courses'),
                                gettext('Popular with learners just like you'),
                                gettext('Guidance from real-world experts'),
                            ]}
                            {...(showSpecialBundlePricingExperimentEnabled
                                ? specialBundleDiscountProps
                                : {})}
                        />
                    </div>

                    {/* Voucher banner for generic bundles: render voucher banner below generic bundle unit */}
                    <div className={styles['voucher-banner-generic-bundle-unit']}>
                        <CertificationExamVoucherBannerWrapper
                            topicId={this.props.topic.id}
                            theme="dark"
                            layout="horizontal"
                            isTopicBanner
                            experimentData={this.props.isTopicVoucherModuleAboveBundle}
                            uiRegion={
                                this.props.isTopicVoucherModuleAboveBundle?.configuration
                                    ?.isTopicVoucherModuleAbovebundle
                                    ? ('above_course_bundle' as VoucherUIRegion)
                                    : ('below_course_bundle' as VoucherUIRegion)
                            }
                            showAddToCart={certVouchersShowAddToCart}
                        />
                    </div>
                </>
            );
        } else if (isMXUser) {
            // Voucher banner for non-English locales: render voucher banner independently from certification bundle unit
            // This allows the voucher banner to show in all languages for MX users, not just English
            return (
                <div className={voucherContainerClass}>
                    <CertificationExamVoucherBannerWrapper
                        topicId={this.props.topic.id}
                        theme="dark"
                        layout="horizontal"
                        isTopicBanner={true}
                        experimentData={this.props.isTopicVoucherModuleAboveBundle}
                        // Send this uiRegion even when there's no certification bundle unit was approved by Anh Dao
                        uiRegion={
                            this.props.isTopicVoucherModuleAboveBundle?.configuration
                                ?.isTopicVoucherModuleAbovebundle
                                ? ('above_course_bundle' as VoucherUIRegion)
                                : ('below_course_bundle' as VoucherUIRegion)
                        }
                        showAddToCart={certVouchersShowAddToCart}
                    />
                </div>
            );
        } else {
            return null;
        }
    }

    get topicEnrollmentStatContent() {
        return !!this.localizedEnrollmentStat ? (
            <TopicEnrollmentStats
                data-item-index={0}
                enrollmentStat={this.localizedEnrollmentStat}
            />
        ) : null;
    }

    get certificationUnitContent() {
        const {udData, topic} = this.props;
        const {Config} = udData;

        const hasOrganizationChina = (Config.brand as BrandWithOrganization)?.organization
            ?.is_enterprise_china;

        const showCertificationUnit =
            isSubsTopicPage(this.pageType) ||
            (isOrgTopicPage(this.pageType) && !hasOrganizationChina);

        return showCertificationUnit ? (
            <CertificationUnit
                data-item-index={0}
                className="component-margin"
                topicId={topic.id}
            />
        ) : null;
    }

    get discoveryListContainerContent() {
        const ref = this.ref;

        const handlePageChange = () => {
            if (serverOrClient.global.scrollY && ref?.current) {
                const y = ref?.current?.getBoundingClientRect().top + serverOrClient.global.scrollY;
                serverOrClient.global.scrollTo?.({
                    top: y,
                    behavior: 'smooth',
                });
            }
        };

        const presetFilters =
            this.props.presetValue === PRESET_FREE
                ? {[AGGREGATION_TYPE_PRICE]: ['price-free']}
                : undefined;

        return (
            <div className="component-margin" data-item-index={-2} ref={this.ref}>
                <TopicDiscoveryListContainerWrapper
                    {...this.props}
                    pageType={this.pageType}
                    presetFilters={presetFilters}
                    handlePageChange={handlePageChange}
                />
            </div>
        );
    }

    get learnMoreSectionContent() {
        const {
            presetValue,
            primaryDescription,
            questionsAndAnswers,
            relatedPages,
            topic,
            isMobile,
            gettext,
            interpolate,
        } = this.props;

        const relatedPagesHeadline = {
            title: interpolate(
                gettext('Free %(topicName)s lessons'),
                {topicName: topic.name},
                true,
            ),
            secondaryText: gettext('Bite-sized learning in minutes'),
            className: styles['section-container'],
            layoutVariant: isMobile ? 'compact' : 'default',
        };

        return (
            <div className={styles['learn-more-section-container']}>
                <LearnMoreSection
                    className={styles['learn-more-section']}
                    topic={topic}
                    primaryDescription={primaryDescription}
                    questionsAndAnswers={questionsAndAnswers}
                    relatedPagesHeadline={{
                        ...relatedPagesHeadline,
                    }}
                    relatedPages={relatedPages}
                    presetValue={presetValue}
                    isMobile={!!isMobile}
                />
            </div>
        );
    }

    discoveryUnitFilter = (unit: Partial<DiscoveryUnit>) => {
        const {presetValue, enableLabsInPersonalPlan, enableAssessmentsInPersonalPlan} = this.props;
        const {Config, me} = this.props.udData;
        const isUBUser = me.is_authenticated && !!me.organization;
        const isUBProUser =
            isUBUser &&
            (Config.features.organization.learning_path.pro_path ||
                (me.is_authenticated && me.organization?.is_pro_license_holder));

        const shouldShowLabs =
            (isSubsTopicPage(this.pageType) && enableLabsInPersonalPlan) || isUBProUser;
        const shouldShowAssessments =
            (isSubsTopicPage(this.pageType) && enableAssessmentsInPersonalPlan) || isUBProUser;

        if (presetValue === PRESET_FREE) {
            return unit.type === 'bestseller_labels';
        }

        /**
         * Currently, if we're using certification banner, we're already showing the related_categories_and_subcategories
         * results inside of it, so we need to filter out from the discovery units container
         * ˝
         * TODO: remove this condition after the related categories and subcategories are removed from the DiscoveryAPI
         */
        if (
            unit.type === 'related_categories_and_subcategories' ||
            unit.type === 'category' ||
            unit.type === 'subcategory'
        ) {
            return false;
        }

        // TODO: CVR-1878 - Remove when recommendation units migration is complete
        // top_instructor unit needs to be fixed in the recommendations API
        if (this.props.isRecommendationsEnabled && unit.type === 'top_instructor') {
            return false;
        }

        if (!shouldShowLabs) {
            return unit?.item_type !== 'lab';
        }
        if (!shouldShowAssessments) {
            return unit?.item_type !== 'assessment';
        }

        // no filter
        return true;
    };

    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    @action
    getOrInitializeDiscoveryUnitStore = (initializationParam?: {
        pageType: string;
        pageObjectId: number;
    }) => {
        if (this.discoveryUnitsStore) {
            return this.discoveryUnitsStore;
        }
        if (!initializationParam) {
            return undefined;
        }
        const {pageType, pageObjectId} = initializationParam;
        this.discoveryUnitsStore = new DiscoveryUnitsContainerStore({pageType, pageObjectId});
        if (!this.discoveryUnitsStore.loading && this.discoveryUnitsStore.firstLoad) {
            this.discoveryUnitsStore.fetchUnits({pageSize: 50});
        }
        return this.discoveryUnitsStore;
    };

    render() {
        const {
            primaryDescription,
            questionsAndAnswers,
            relatedPages,
            topic,
            isRecommendationsEnabled,
            recommendationUnits,
            isRecommendationsLoading,
            onLoadMore,
            isConsumerSubsSubscriber,
            showPersonalPlanBadge,
            showCodingExercisesBadge,
        } = this.props;

        const showLearnMoreSection =
            primaryDescription || relatedPages.length > 1 || questionsAndAnswers.length > 0;

        const pageObjectId = getPageObjectIdFromTopic(topic);

        // TODO: CVR-1878 - Remove when recommendation units migration is complete
        let isDiscoveryUnitsLoading: boolean | undefined;

        if (isRecommendationsEnabled) {
            isDiscoveryUnitsLoading = this.arePageDataLoading || isRecommendationsLoading;
        } else {
            if (!this.arePageDataLoading) {
                this.getOrInitializeDiscoveryUnitStore({
                    pageType: this.pageType,
                    pageObjectId,
                });
            }
            isDiscoveryUnitsLoading =
                this.arePageDataLoading || this.getOrInitializeDiscoveryUnitStore()?.firstLoad;
        }

        // TODO: CVR-1878 - Remove when recommendation units migration is complete
        const discoveryUnitsProps = isRecommendationsEnabled
            ? {
                  units: recommendationUnits as DiscoveryUnit[],
                  disableInfiniteScroll: true as const,
                  onLoadMore,
              }
            : {
                  store: this.getOrInitializeDiscoveryUnitStore({
                      pageType: this.pageType,
                      pageObjectId,
                  }),
                  /**
                   * This is a temporary solution for handling the infinite scroll with the
                   * children that injected from outside. So rather than using the infinite scroll
                   * and fetching more units after the last unit, we're just fetching the units
                   * all together and showing them in the page.
                   *
                   * Problem in here is that, we're injecting a unit from outside to as last unit,
                   * and we're trying to fetch a new unit in the middle of the page.
                   */
                  fetchOptions: {pageSize: 50},
              };

        /**
         * Provider of Mobx requires immutable props, in order to avoid below problem, don't forget to wait for
         * page data to be loaded or just don't wrap with it until the page data is loaded.
         * "MobX Provider: The set of provided stores has changed." error in loading stage.
         * https://unpkg.com/browse/mobx-react@7.2.1/src/Provider.tsx
         */

        const mobxProviderProps = {
            isConsumerSubsSubscriber,
            showPersonalPlanBadge,
            showCodingExercisesBadge,
        };
        return (
            <>
                {this.showCertificationBanner && this.renderCertificationBanner()}
                {this.showContextualHeader && this.renderContextualHeader()}
                <div className="ud-container ud-page-wrapper">
                    {!this.arePageDataLoading
                        ? !this.hasReplacementTitle && this.renderTitle()
                        : !this.hasReplacementTitle && this.renderLoadingTitle()}
                    <AsyncPriceCourseCardContext.Provider value={{}}>
                        {!this.hasReplacementTitle && this.renderCategoryUnits()}
                        {this.renderStaticUnits()}
                    </AsyncPriceCourseCardContext.Provider>
                    {this.renderCourseGuidanceBanner()}
                    <EntryPointPopUp />
                    {!isDiscoveryUnitsLoading ? (
                        <Provider {...mobxProviderProps}>
                            <ResourceContextMenuProvider
                                resourceContextMenuProps={this.resourceContextMenuProps}
                            >
                                <AsyncPriceCourseCardContext.Provider value={{}}>
                                    <TopicDiscoveryUnitsContainerWrapper
                                        {...this.props}
                                        pageType={this.pageType}
                                        discoveryUnitFilter={this.discoveryUnitFilter}
                                        {...discoveryUnitsProps}
                                    >
                                        {!this.hasReplacementTitle &&
                                            this.topicEnrollmentStatContent}
                                        {this.certificationUnitContent}
                                        {this.discoveryListContainerContent}
                                        <ValueProps className="component-margin" />
                                    </TopicDiscoveryUnitsContainerWrapper>
                                </AsyncPriceCourseCardContext.Provider>
                            </ResourceContextMenuProvider>
                        </Provider>
                    ) : (
                        this.renderDiscoveryUnitsLoader()
                    )}

                    {showLearnMoreSection && (
                        <MarketplaceOnly>{this.learnMoreSectionContent}</MarketplaceOnly>
                    )}
                </div>
            </>
        );
    }
}

export const Topic = withI18n(withUDData(withRouter(InternalTopic)));
